import React from "react";
import { ExclamationCircleIcon } from "@heroicons/react/16/solid";
import { cn } from "@utils";

const Input = ({
  id,
  name,
  type,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  onSelect,
  className,
  placeholder,
  control,
  step,
  required,
  innerRef,
  validate,
  checked,
  multiple,
  disabled,
  invalid,
  prefix,
  readonly,
  accept,
  role,
  maxLength,
  rows,
}) => {
  const outline = disabled ? "zinc-200" : invalid ? "red-300" : "zinc-300";

  const outlineFocus = disabled ? "zinc-200" : invalid ? "red-600" : "primary";

  const bg = disabled ? "zinc-50" : invalid ? "red" : "white";

  const textColor = disabled ? "zinc-500" : invalid ? "red-900" : "black";

  const placeholderTextColor = disabled
    ? "zinc-200"
    : invalid
      ? "red-300"
      : "zinc-400";

  return (
    <div
      className={`
        relative 
        rounded-md
        bg-${bg}
        outline outline-1
        -outline-offset-1
        outline-${outline}
        focus-within:outline
        focus-within:outline-1
        focus-within:-outline-offset-2
        focus-within:outline-${outlineFocus}
      `}
    >
      {/* Prefix */}
      {prefix && (
        <div
          className={`
            ${type === "file" && "border-r border-zinc-300"}
            ${type === "file" && "pr-3"} 
            pointer-events-none
            absolute
            inset-y-0
            left-0
            flex
            items-center
            pl-3
            select-none
            text-base
            text-zinc-500
            sm:text-sm/6
          `}
        >
          {prefix}
        </div>
      )}

      {type === "textarea" ? (
        <textarea
          id={id}
          name={name}
          type={type}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onSelect={onSelect}
          placeholder={placeholder}
          control={control}
          step={step}
          required={required}
          ref={innerRef}
          validate={validate}
          checked={checked}
          disabled={disabled}
          multiple={multiple}
          accept={accept}
          maxLength={maxLength}
          rows={rows}
          className={`
          block
          w-full
          py-2
          ${type === "file" ? "pl-32" : prefix ? "pl-8" : "pl-3"} 
          ${invalid ? "pr-8" : "pr-3"}
          text-sm
          text-${textColor}
          placeholder:text-${placeholderTextColor}
          focus:outline-none
          bg-${bg}
          file:hidden
          hover:file:bg-zinc-200
          ${className || ""}
        `}
        />
      ) : (
        <input
          id={id}
          role={role}
          name={name}
          type={type}
          value={value}
          readOnly={readonly}
          defaultValue={defaultValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          onSelect={onSelect}
          placeholder={placeholder}
          control={control}
          step={step}
          required={required}
          ref={innerRef} // if you're using React's ref
          validate={validate}
          checked={checked}
          disabled={disabled}
          multiple={multiple}
          accept={accept}
          maxLength={maxLength}
          className={cn(
            "block w-full py-2 text-sm focus:outline-none file:hidden hover:file:bg-zinc-200",
            type === "file" ? "pl-32" : prefix ? "pl-8" : "pl-3",
            type === "date" && "[&::-webkit-calendar-picker-indicator]:hidden",
            invalid ? "pr-8" : "pr-3",
            `text-${textColor}`,
            `placeholder:text-${placeholderTextColor}`,
            `bg-${bg}`,
            className,
          )}
        />
      )}

      {/* Error Icon */}
      {invalid && (
        <div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            flex
            items-center
            pr-3
          "
        >
          <ExclamationCircleIcon
            aria-hidden="true"
            className="
              h-5
              w-5
              text-red-500
              sm:h-4
              sm:w-4
            "
          />
        </div>
      )}
    </div>
  );
};

export default Input;
